import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
} from "react";

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

type Variant = "primary" | "secondary" | "danger" | "ghost";
const variants: Record<Variant, string> = {
  primary: "bg-sky-600 hover:bg-sky-500 text-white",
  secondary: "bg-slate-700 hover:bg-slate-600 text-slate-100",
  danger: "bg-red-600 hover:bg-red-500 text-white",
  ghost: "bg-transparent hover:bg-slate-800 text-slate-200",
};

export function Button({
  variant = "primary",
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
        variants[variant],
        className,
      )}
      {...props}
    />
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-sky-500",
        className,
      )}
      {...props}
    />
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn("rounded-lg border border-slate-800 bg-slate-900/60 p-4", className)}>
      {children}
    </div>
  );
}

export function Badge({
  children,
  color = "#334155",
  className,
}: {
  children: ReactNode;
  color?: string;
  className?: string;
}) {
  return (
    <span
      className={cn("inline-flex items-center rounded px-2 py-0.5 text-xs font-medium", className)}
      style={{ backgroundColor: color, color: "#fff" }}
    >
      {children}
    </span>
  );
}

type BannerKind = "info" | "warn" | "danger" | "success";
const bannerStyles: Record<BannerKind, string> = {
  info: "border-sky-700 bg-sky-950/60 text-sky-200",
  warn: "border-amber-700 bg-amber-950/60 text-amber-200",
  danger: "border-red-700 bg-red-950/60 text-red-200",
  success: "border-emerald-700 bg-emerald-950/60 text-emerald-200",
};
export function Banner({
  kind = "info",
  className,
  children,
}: {
  kind?: BannerKind;
  className?: string;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("rounded-md border px-4 py-3 text-sm", bannerStyles[kind], className)}>
      {children}
    </div>
  );
}
